import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { words, revealWords, settleWords } from '../../../components/text';

/** Bottom to top. Examples stay conceptual: no logo wall. */
const LAYERS = [
  { name: 'Infrastructure', examples: ['Cloud', 'Containers', 'CI/CD', 'Monitoring'], from: 0 },
  { name: 'Integrations', examples: ['APIs', 'GitHub', 'Jira', 'Cloud services', 'External systems'], from: 0 },
  { name: 'Intelligence', examples: ['AI', 'Agents', 'Automation', 'Data processing'], from: 1 },
  { name: 'Platform', examples: ['Workflows', 'Security', 'Business logic'], from: 1 },
  { name: 'Experience', examples: ['Web', 'Mobile', 'Interactive interfaces'], from: 2 },
];
const TOP = LAYERS.length - 1;
const CX = 560;
const W = 300;
const D = 110;
const T = 18;

/** Vertical position of each layer: stacked while building, exploded to show depth, collapsed to show simplicity. */
const layerY = (i: number, step: number): number => {
  if (step === 3) return 660 - i * 115;
  if (step >= 4) return 470 - i * 22;
  return 620 - i * 100;
};

/**
 * 0 Infrastructure and integrations
 * 1 Intelligence and platform
 * 2 Experience on top
 * 3 The stack explodes — COMPLEX UNDERNEATH.
 * 4 It collapses; only the surface stays lit — SIMPLE WHERE IT MATTERS.
 */
export class TechnologyScene extends BaseScene {
  public readonly stepCount = 5;

  protected template(): string {
    const slabs = LAYERS.map((layer, i) => `
      <g class="tech-layer${i === TOP ? ' tech-layer--surface' : ''}" data-layer="${i}">
        <g class="tech-layer__motion">
          <path class="tech-slab__side" d="M${CX - W} 0 L${CX} ${D} L${CX + W} 0 L${CX + W} ${T} L${CX} ${D + T} L${CX - W} ${T} Z"/>
          <path class="tech-slab__top" d="M${CX - W} 0 L${CX} ${-D} L${CX + W} 0 L${CX} ${D} Z"/>
          <path class="tech-slab__inset" d="M${CX - W * 0.72} 0 L${CX} ${-D * 0.72} L${CX + W * 0.72} 0 L${CX} ${D * 0.72} Z"/>
          <path class="tech-slab__leader" d="M${CX + W + 16} 0 H${CX + W + 70}"/>
          <text class="tech-layer__name" x="${CX + W + 86}" y="-4">${layer.name.toUpperCase()}</text>
          <text class="tech-layer__examples" x="${CX + W + 88}" y="24">${layer.examples.join('  ·  ')}</text>
        </g>
      </g>`).join('');

    return `
      <div class="scene-layout technology">
        <svg class="diagram tech-diagram" viewBox="0 0 1600 900" role="img" aria-label="Five layers: infrastructure, integrations, intelligence, platform and experience">${slabs}</svg>
        <div class="tech-statements">
          <h2 class="statement" data-from="3" data-until="3">${words('COMPLEX UNDERNEATH.')}</h2>
          <h2 class="statement statement--accent" data-from="4">${words('SIMPLE WHERE IT MATTERS.')}</h2>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    LAYERS.forEach((layer, i) => {
      if (layer.from !== 0) return;
      tl.fromTo(this.$(`.tech-layer[data-layer="${i}"] .tech-layer__motion`), { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out' }, 0.2 + i * 0.3);
    });
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('technology', step, immediate);

    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();
    const collapsed = step >= 4;

    this.$$('.tech-layer').forEach((el, i) => {
      const layer = LAYERS[i];
      const motion = el.querySelector('.tech-layer__motion')!;
      const visible = step >= layer.from;
      // The group owns position; the inner motion group owns arrival.
      gsap.to(el, { y: layerY(i, step), duration: d * (step >= 3 ? 1.3 : 0.9), ease: 'expo.inOut', overwrite: 'auto' });
      if (forward && step === layer.from) {
        tl.fromTo(motion, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out' }, (i - LAYERS.findIndex((l) => l.from === step)) * 0.3);
      } else {
        gsap.set(motion, { opacity: visible ? 1 : 0, y: 0 });
      }
      el.classList.toggle('is-quiet', collapsed && i !== TOP);
    });

    this.$('.tech-diagram').dataset.state = step === 3 ? 'exploded' : collapsed ? 'collapsed' : 'building';
    this.$$('.tech-statements .statement').forEach((s) => settleWords(s));
    if (forward && step >= 3) tl.add(revealWords(this.$$('.tech-statements .statement')[step - 3]), 0.5);
  }
}
